import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, UserPlus, KeyRound, Trash2, Copy, Check, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function TeamPage() {
  const { role, email: currentEmail } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [creating, setCreating] = useState(false);
  // One-time credential to display in a banner AFTER create/reset. The
  // plaintext password is intentionally not persisted anywhere else.
  const [issuedCred, setIssuedCred] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadMembers = async () => {
    try {
      const r = await api.get("/v1/team");
      setMembers(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (role !== "owner") {
      setLoading(false);
      return;
    }
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Owner-only page: partner accounts get bounced back to the dashboard root
  // (the sidebar link is also hidden from partners, so this is defense-in-depth).
  if (role !== "owner") return <Navigate to="/operator" replace />;

  const handleCreate = async (e) => {
    e.preventDefault();
    const em = inviteEmail.trim().toLowerCase();
    if (!em.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/v1/team", { email: em });
      setIssuedCred({
        email: r.data.email,
        password: r.data.one_time_password,
        kind: "created",
      });
      setInviteEmail("");
      await loadMembers();
      toast.success(`Partner ${r.data.email} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add partner");
    } finally {
      setCreating(false);
    }
  };

  const handleReset = async (m) => {
    if (!window.confirm(`Reset password for ${m.email}? Their existing password will stop working immediately.`)) return;
    try {
      const r = await api.post(`/v1/team/${m.id}/reset-password`);
      setIssuedCred({
        email: r.data.email,
        password: r.data.one_time_password,
        kind: "reset",
      });
      toast.success(`New password issued for ${r.data.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete ${m.email}? This immediately revokes their access.`)) return;
    try {
      await api.delete(`/v1/team/${m.id}`);
      await loadMembers();
      toast.success(`${m.email} removed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const copyCred = async () => {
    if (!issuedCred) return;
    const text = `Email: ${issuedCred.email}\nPassword: ${issuedCred.password}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please select the text manually");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="team-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-500 mb-1">
            Access Control
          </div>
          <h1 className="text-2xl font-display font-black tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6" /> Team & Partner Logins
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Partners can use every feature but cannot manage this Team page or delete accounts.
            You are always the sole owner unless you explicitly promote someone.
          </p>
        </div>
      </div>

      {/* One-time credential banner (shown right after create or reset) */}
      {issuedCred && (
        <div
          className="border-2 border-blue-600 bg-blue-50 p-5 shadow-sm"
          data-testid="issued-credential-banner"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-blue-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-blue-800 mb-2">
                {issuedCred.kind === "reset" ? "New Password Issued" : "New Partner Created"}{" "}
                · Copy Now · Shown Only Once
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-sm">
                <div className="text-zinc-600">Email:</div>
                <div className="font-bold" data-testid="issued-email">
                  {issuedCred.email}
                </div>
                <div className="text-zinc-600">Password:</div>
                <div className="font-bold tracking-wider" data-testid="issued-password">
                  {issuedCred.password}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  onClick={copyCred}
                  size="sm"
                  className="h-8 gap-1.5"
                  data-testid="copy-credential-button"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy email + password"}
                </Button>
                <Button
                  onClick={() => setIssuedCred(null)}
                  size="sm"
                  variant="outline"
                  className="h-8"
                  data-testid="dismiss-credential-button"
                >
                  I've saved it — dismiss
                </Button>
              </div>
              <div className="text-[11px] text-zinc-600 mt-2">
                This password will not be shown again. If lost, use "Reset password" on the row below.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add-partner form */}
      <form
        onSubmit={handleCreate}
        className="border border-zinc-300 p-5 bg-white"
        data-testid="add-partner-form"
      >
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-500 mb-3">
          Add a Partner Account
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-xs font-bold">Partner email</Label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="partner@yourcompany.com"
              className="mt-1"
              data-testid="partner-email-input"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={creating || !inviteEmail.includes("@")}
            className="gap-2 h-10"
            data-testid="add-partner-button"
          >
            <UserPlus className="w-4 h-4" />
            {creating ? "Creating…" : "Add Partner"}
          </Button>
        </div>
        <div className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
          A random 16-character password is generated and shown once. Copy it and share
          with the partner through your usual secure channel. They can log in at{" "}
          <code className="bg-zinc-100 px-1">/login</code> and will see everything except
          this Team page.
        </div>
      </form>

      {/* Members table */}
      <div className="border border-zinc-300 bg-white" data-testid="team-members-list">
        <div className="px-5 py-3 border-b border-zinc-200 text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-500 flex items-center justify-between">
          <span>Current Team ({members.length})</span>
          {loading && <span className="text-zinc-400 normal-case">Loading…</span>}
        </div>
        {members.length === 0 && !loading && (
          <div className="p-8 text-center text-sm text-zinc-500">
            No team members yet — add a partner above.
          </div>
        )}
        <ul className="divide-y divide-zinc-200">
          {members.map((m) => {
            const isOwner = m.role === "owner";
            const isSelf = m.email.toLowerCase() === (currentEmail || "").toLowerCase();
            return (
              <li
                key={m.id}
                className="px-5 py-4 flex flex-wrap items-center gap-3"
                data-testid={`team-member-${m.email}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm font-medium truncate">
                    {m.email}
                    {isSelf && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                        (you)
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    Added {new Date(m.created_at).toLocaleDateString()}
                  </div>
                </div>
                <span
                  className={`text-[9px] font-bold uppercase tracking-[0.24em] px-2 py-0.5 border ${
                    isOwner
                      ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                      : "border-amber-500 text-amber-700 bg-amber-50"
                  }`}
                >
                  {m.role}
                </span>
                {!isSelf && !isOwner && (
                  <>
                    <Button
                      onClick={() => handleReset(m)}
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5"
                      data-testid={`reset-password-${m.email}`}
                    >
                      <KeyRound className="w-3.5 h-3.5" /> Reset password
                    </Button>
                    <Button
                      onClick={() => handleDelete(m)}
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5 text-red-700 border-red-300 hover:bg-red-50 hover:text-red-800"
                      data-testid={`delete-partner-${m.email}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </Button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
